import React, { RefObject } from 'react';
import {
  ConfigProvider,
  Select as AntSelect,
  SelectProps as AntSelectProps,
  RefSelectProps,
  Tag,
  TagProps,
} from 'antd';
import type { InputStatus } from 'antd/es/_util/statusUtils';
import AntLibSelect, { SelectValue } from 'antd/lib/select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import styled, { css, createGlobalStyle } from 'styled-components';
import { nextSelectTheme } from '../next/tokens';

export type { RefSelectProps } from 'antd';

export type { SelectValue } from 'antd/lib/select';

const { Option, OptGroup } = AntLibSelect;

export type { DefaultOptionType, SelectProps as BaseSelectProps } from 'antd/es/select';

const UpDownCaretsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5.33203 5.33366L7.99936 2.66699L10.6667 5.33366"
      stroke="currentColor"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M5.33203 10.6663L7.99936 13.333L10.6667 10.6663"
      stroke="currentColor"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export type SelectFieldVariant = 'badge' | 'next';

export interface SelectProps<T> extends AntSelectProps<T> {
  hasError?: boolean;
  width?: string;
  variant?: SelectFieldVariant;
  inputRef?: RefObject<RefSelectProps>;
}

const variantBadgeEditableStyles = css(
  ({ theme }) => `
  width: auto!important;
  
  .ant-select-arrow {
    color: ${theme.token.colorPrimary}!important;
  }
  
  .ant-select-selection-item {
    color: ${theme.token.colorPrimary}!important;
    font-weight: 600;
   
  }
`,
);

const variantBadgeDisabledStyles = css(
  ({ theme }) => `
  width: auto!important;

  .ant-select-arrow {
    color: ${theme.token.colorPrimary}!important;
  }

  .ant-select-selection-item {
    color: ${theme.token['green.6']}!important;
    font-weight: 600;
  }

  .ant-select-selector {
    background-color: ${theme.token['green.1']}!important;
    border-color: ${theme.token['green.3']}!important;
  }
`,
);

/**
 * Minimal CSS for the "next" variant — only for things ConfigProvider tokens can't express.
 * Everything else (height, border-radius, colors, focus ring, error ring, multi-item styling)
 * is handled by the ConfigProvider theme in nextSelectTheme.
 */
const variantNextMinimalStyles = css`
  /* Hover: match Input hover (Select has no hoverBorderColor token).
   * Uses !important on border-color to beat Ant's selector specificity. */
  &:hover:not(.ant-select-disabled):not(.ant-select-focused):not(.ant-select-status-error)
    .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorBorderHover} !important;
    box-shadow: ${({ theme }) => theme.token.next.boxShadowHover};
  }

  /* Focus and (Focus + hover): keep focus styles (exclude error state) */
  &.ant-select-focused:not(.ant-select-status-error) .ant-select-selector,
  &.ant-select-focused:not(.ant-select-status-error):hover .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorBorderFocus} !important;
    box-shadow: ${({ theme }) => theme.token.next.boxShadowFocus} !important;
  }

  /* Error: keep error border */
  &.ant-select-status-error .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorError} !important;
  }

  /* Error + hover / Error + focus / Error + focus + hover: use error shadow */
  &.ant-select-status-error:hover:not(.ant-select-disabled) .ant-select-selector,
  &.ant-select-status-error.ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorError} !important;
    box-shadow: ${({ theme }) => theme.token.next.boxShadowError} !important;
  }

  /* Disabled: border matches bg (Ant keeps border visible on disabled) */
  &.ant-select-disabled .ant-select-selector {
    border-color: ${({ theme }) => theme.token.next.colorBgDisabled} !important;
    box-shadow: none !important;
  }

  /* Disabled with value: font-weight 500; placeholder stays at default 400 */
  &.ant-select-disabled .ant-select-selection-item {
    font-weight: ${({ theme }) => theme.token.next.fontWeight500} !important;
  }
`;

const NEXT_DROPDOWN_CLASS = 'ant-select-dropdown-next';

/**
 * Minimal dropdown CSS — only for chrome that Ant doesn't expose as tokens.
 * Item height, padding, font-size, selected/active bg are all handled by ConfigProvider.
 */
const NextSelectDropdownStyles = createGlobalStyle`
  .${NEXT_DROPDOWN_CLASS}.ant-select-dropdown {
    border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.token.next.colorBorder};
    padding: 6px;

    .rc-virtual-list-holder-inner {
      gap: 2px;
    }

    .ant-select-item {
      color: ${({ theme }) => theme.token.next.palette.grey700};
    }

    .ant-select-item-option-content {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .ant-select-item-option-state {
      flex-shrink: 0;
      margin-left: 8px;
    }
  }
`;

const StyledSelect = styled(AntSelect)<SelectProps<unknown>>`
  --fa-animation-duration: 0.8s;

  .ant-select-selection-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-select-selection-item:has(> div) {
    display: flex;
  }

  .ant-select-arrow {
    color: ${({ theme, disabled }) =>
      disabled ? theme.token.colorTextDisabled : theme.token.colorTextBase};
  }

  ${({ variant, disabled }) =>
    variant === 'badge' && (disabled ? variantBadgeDisabledStyles : variantBadgeEditableStyles)};

  ${({ variant }) => variant === 'next' && variantNextMinimalStyles};
` as typeof AntSelect;

const getVariantProps = <T extends SelectValue = SelectValue>(
  variant?: SelectFieldVariant,
  status?: InputStatus,
  disabled?: boolean,
): Partial<SelectProps<T>> => {
  const variantProps: Partial<SelectProps<T>> = {};

  switch (variant) {
    case 'badge':
      variantProps.className = `ant-select-variant-${variant}`;
      variantProps.bordered = Boolean(disabled || status);
      variantProps.suffixIcon = disabled ? null : (
        <FontAwesomeIcon icon={solid('caret-down')} size="lg" />
      );
      break;
    case 'next':
      variantProps.className = `ant-select-variant-${variant}`;
      variantProps.popupClassName = NEXT_DROPDOWN_CLASS;
      variantProps.menuItemSelectedIcon = <FontAwesomeIcon icon={regular('check')} />;
      break;
    default:
      break;
  }

  return variantProps;
};

const Select = <T extends SelectValue = SelectValue>({
  popupMatchSelectWidth = false,
  hasError,
  status,
  width = '100%',
  variant,
  size = variant === 'next' ? 'middle' : 'large',
  inputRef,
  ...rest
}: SelectProps<T>) => {
  const variantProps: Partial<SelectProps<T>> = getVariantProps(variant, status, rest?.disabled);

  /* eslint-disable no-nested-ternary */
  const suffixIcon = rest.loading ? (
    <FontAwesomeIcon icon={regular('spinner-third')} spin speed="0.1s" />
  ) : variant === 'next' && rest.open === false ? null : variant === 'next' ? (
    <UpDownCaretsIcon />
  ) : (
    <FontAwesomeIcon icon={solid('caret-down')} />
  );

  const selectElement = (
    <>
      {variant === 'next' && <NextSelectDropdownStyles />}
      <StyledSelect
        size={size}
        status={hasError ? 'error' : status}
        popupMatchSelectWidth={popupMatchSelectWidth}
        {...rest}
        variant={variant}
        {...variantProps}
        style={{ width, ...rest.style }}
        suffixIcon={suffixIcon}
        ref={inputRef}
      />
    </>
  );

  if (variant === 'next') {
    return <ConfigProvider theme={nextSelectTheme}>{selectElement}</ConfigProvider>;
  }

  return selectElement;
};

const SelectTag: React.FC<TagProps> = (props) => (
  <Tag closable closeIcon={<FontAwesomeIcon icon={regular('close')} />} {...props} />
);

export { Option, Select, SelectTag, OptGroup };
